import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function KotOutletMaster({ token, selectedProperty, onOutletChanged }) {
  const ORDER_TYPE_OPTIONS = [
    { code: 'IN_ROOM_DINING', label: 'IN ROOM DINING' },
    { code: 'TAKE_AWAY', label: 'TAKE AWAY' },
    { code: 'ROOM_SERVICE', label: 'ROOM SERVICE' }
  ];
  const [scope, setScope] = useState({
    chain_id: selectedProperty?.chain_id || '',
    property_id: selectedProperty?.id || ''
  });
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [form, setForm] = useState({
    code: '',
    name: '',
    bill_prefix: '',
    billing_name: '',
    billing_address: '',
    city: '',
    state: '',
    country: '',
    gst_number: '',
    kot_order_types: ['IN_ROOM_DINING', 'TAKE_AWAY', 'ROOM_SERVICE'],
    outlet_type: 'BAR',
    module_kot_enabled: true,
    module_bar_enabled: true,
    module_banquet_enabled: false,
    is_active: true
  });

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': scope.chain_id,
    'x-property-id': scope.property_id,
    ...extra
  });

  const loadOutlets = async () => {
    if (!scope.property_id) return setRows([]);
    try {
      const res = await fetch(`/api/v1/core/outlets?property_id=${encodeURIComponent(scope.property_id)}&include_inactive=true`, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load outlets');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Network error while loading outlets');
    }
  };

  useEffect(() => {
    if (selectedProperty?.chain_id && selectedProperty?.id) {
      setScope((prev) => ({ ...prev, chain_id: selectedProperty.chain_id, property_id: selectedProperty.id }));
    } else {
      setScope({ chain_id: '', property_id: '' });
      setRows([]);
    }
  }, [selectedProperty]);

  useEffect(() => { loadOutlets(); }, [scope.property_id]);

  const saveOutlet = async () => {
    setError('');
    if (!scope.chain_id || !scope.property_id) return setError('Select hotel property first from header');
    if (!String(form.code || '').trim() || !String(form.name || '').trim()) return setError('Code and Name are required');
    if (!Array.isArray(form.kot_order_types) || form.kot_order_types.length === 0) return setError('Select at least one Order Type');
    try {
      const res = await fetch(editingId ? `/api/v1/core/outlets/${editingId}` : '/api/v1/core/outlets', {
        method: editingId ? 'PUT' : 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage(editingId ? 'Outlet updated successfully' : 'Outlet created successfully');
      setEditingId('');
      setForm({
        code: '',
        name: '',
        bill_prefix: '',
        billing_name: '',
        billing_address: '',
        city: '',
        state: '',
        country: '',
        gst_number: '',
        kot_order_types: ['IN_ROOM_DINING', 'TAKE_AWAY', 'ROOM_SERVICE'],
        outlet_type: 'BAR',
        module_kot_enabled: true,
        module_bar_enabled: true,
        module_banquet_enabled: false,
        is_active: true
      });
      loadOutlets();
      onOutletChanged?.();
    } catch {
      setError('Network error');
    }
  };

  const editOutlet = (row) => {
    setEditingId(row.id);
    setError('');
    setMessage('');
    setForm({
      code: row.code || '',
      name: row.name || '',
      bill_prefix: row.bill_prefix || '',
      billing_name: row.billing_name || '',
      billing_address: row.billing_address || '',
      city: row.city || '',
      state: row.state || '',
      country: row.country || '',
      gst_number: row.gst_number || '',
      kot_order_types: Array.isArray(row.kot_order_types) && row.kot_order_types.length
        ? row.kot_order_types
        : ['IN_ROOM_DINING', 'TAKE_AWAY', 'ROOM_SERVICE'],
      outlet_type: row.outlet_type || 'BAR',
      module_kot_enabled: row.module_kot_enabled === true,
      module_bar_enabled: row.module_bar_enabled !== false,
      module_banquet_enabled: row.module_banquet_enabled === true,
      is_active: row.is_active !== false
    });
    scrollToForm();
  };

  const deactivateOutlet = async (id, name) => {
    if (!window.confirm(`Deactivate outlet "${name}"? This will hide it from all KOT & POS screens.`)) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/core/outlets/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Deactivate failed');
      loadOutlets();
      onOutletChanged?.();
    } catch {
      setError('Network error');
    }
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const btnStyle = { padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>KOT Outlet Master</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Outlet' : 'Add Outlet'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.7rem' }}>
          <input placeholder="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} style={inputStyle} />
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          <input placeholder="Bill Prefix (e.g. RST, CS, BANQ)" value={form.bill_prefix} onChange={(e) => setForm({ ...form, bill_prefix: e.target.value })} style={inputStyle} />
          <select value={form.outlet_type} onChange={(e) => setForm({ ...form, outlet_type: e.target.value })} style={inputStyle}>
            <option value="BAR">BAR</option>
            <option value="RESTAURANT">RESTAURANT</option>
            <option value="COFFEE_SHOP">COFFEE_SHOP</option>
            <option value="BANQUET">BANQUET</option>
          </select>
          <input placeholder="Billing Name (optional)" value={form.billing_name} onChange={(e) => setForm({ ...form, billing_name: e.target.value })} style={inputStyle} />
          <input placeholder="Tax No / GST No (optional)" value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} style={inputStyle} />
          <input placeholder="City (optional)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} style={inputStyle} />
          <input placeholder="State (optional)" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} style={inputStyle} />
          <input placeholder="Country (optional)" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} style={inputStyle} />
          <input placeholder="Billing Address (optional)" value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} style={{ ...inputStyle, gridColumn: '1 / -1' }} />
          <div style={{ gridColumn: '1 / -1', background: '#fff', border: '1px solid #dcdde1', borderRadius: '6px', padding: '0.55rem 0.7rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.45rem', color: '#2d3748' }}>Allowed Order Types for this Outlet</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.7rem' }}>
              {ORDER_TYPE_OPTIONS.map((opt) => {
                const checked = Array.isArray(form.kot_order_types) && form.kot_order_types.includes(opt.code);
                return (
                  <label key={opt.code} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const current = Array.isArray(form.kot_order_types) ? form.kot_order_types : [];
                        const next = e.target.checked
                          ? [...new Set([...current, opt.code])]
                          : current.filter((v) => v !== opt.code);
                        setForm({ ...form, kot_order_types: next });
                      }}
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.module_kot_enabled} onChange={(e) => setForm({ ...form, module_kot_enabled: e.target.checked })} />
            KOT Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.module_bar_enabled} onChange={(e) => setForm({ ...form, module_bar_enabled: e.target.checked })} />
            BAR Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.module_banquet_enabled} onChange={(e) => setForm({ ...form, module_banquet_enabled: e.target.checked })} />
            Banquet Enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </label>
        </div>
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={saveOutlet} className="btn-primary" style={btnStyle}>{editingId ? 'Update' : 'Add'}</button>
          {editingId && (
            <button
              onClick={() => {
                setEditingId('');
                setForm({
                  code: '',
                  name: '',
                  bill_prefix: '',
                  billing_name: '',
                  billing_address: '',
                  city: '',
                  state: '',
                  country: '',
                  gst_number: '',
                  kot_order_types: ['IN_ROOM_DINING', 'TAKE_AWAY', 'ROOM_SERVICE'],
                  outlet_type: 'BAR',
                  module_kot_enabled: true,
                  module_bar_enabled: true,
                  module_banquet_enabled: false,
                  is_active: true
                });
              }}
              className="btn-muted" style={btnStyle}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar">
        <h3>Outlets</h3>
        {rows.length === 0 ? (
          <p>No outlets found for this property</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Code</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Bill Prefix</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Billing Name</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Tax No</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Order Types</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>KOT</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>BAR</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Active</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.code}</td>
                  <td style={{ padding: '0.6rem' }}>{r.name}</td>
                  <td style={{ padding: '0.6rem' }}>{r.bill_prefix || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.billing_name || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.gst_number || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{Array.isArray(r.kot_order_types) && r.kot_order_types.length ? r.kot_order_types.join(', ') : '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.outlet_type}</td>
                  <td style={{ padding: '0.6rem' }}>{r.module_kot_enabled ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.module_bar_enabled ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.is_active ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => editOutlet(r)} className="btn-primary" style={btnStyle}>Edit</button>
                    <button onClick={() => deactivateOutlet(r.id, r.name)} className="btn-danger" style={btnStyle}>Deactivate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default KotOutletMaster;
